import { Button, Card, CloseButton, Divider, Drawer, Group, Select, Stack, Title, useMantineTheme, Box, Tabs, Space } from '@mantine/core';
import { useNav } from '@root/Services/NavigationService';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { Savings } from './Savings';
import { Coverage } from './Coverage';
import { Inventory } from './Inventory';
import { TooltipWhite, useResizeNeeded } from '@root/Design/Primitives';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDi, useDiMemo } from '@root/Services/DI';
import { FormatService, useFmtSvc } from '@root/Services/FormatService';
import { useDisclosure } from '@mantine/hooks';
import { addDays, addMonths, startOfMonth } from 'date-fns';
import { DiscountQueryService } from '@root/Services/Invoices/DiscountQueryService';
import { useFullHeightTabs } from '@root/Design/ActionPage';
import { FillerSwitch } from '@root/Design/Filler';
import { FilterItem, FilterToken } from '@root/Components/Filter/Design';
import { useAsync } from '@react-hookz/web';
import { DashboardPersistenceService, IDashboardConfigBase } from '@root/Components/DashboardPersistence/DashboardPersistenceService';

interface RangePickerItem {
    month: Date;
}

export interface RangeSelection {
    months: Date[];
    inclFrom: Date;
    inclTo: Date;
    exclTo: Date;
    fromMonth: Date;
    toMonth: Date;
}

const createSelectionRange = (months: Date[]): RangeSelection => {
    if (months.length === 0) {
        return { months: [], inclFrom: new Date(), inclTo: new Date(), exclTo: new Date(), fromMonth: new Date(), toMonth: new Date() };
    }
    const sortedMonths = [...months].sort((a, b) => a.getTime() - b.getTime());
    const inclFrom = startOfMonth(sortedMonths[0]);
    const exclTo = addMonths(startOfMonth(sortedMonths[sortedMonths.length - 1]), 1);
    const inclTo = addDays(exclTo, -1);
    return {
        months: sortedMonths,
        inclFrom,
        inclTo,
        exclTo,
        fromMonth: sortedMonths[0],
        toMonth: sortedMonths[sortedMonths.length - 1],
    };
};

function reviveRangeSelection(raw: RangeSelection): RangeSelection {
    return {
        months: raw.months.map((d) => new Date(d)),
        inclFrom: new Date(raw.inclFrom),
        inclTo: new Date(raw.inclTo),
        exclTo: new Date(raw.exclTo),
        fromMonth: new Date(raw.fromMonth),
        toMonth: new Date(raw.toMonth),
    };
}

export function DiscountsPage() {
    const nav = useNav();
    const dashboardSvc = useDi(DashboardPersistenceService);
    const { tab } = useNav().getData('tab');
    const activeTab = tab ?? 'savings';
    const resizeNeeded = useResizeNeeded();
    const handleTabChange = (tabName: string) => {
        nav.replaceParams({ tab: tabName });
        resizeNeeded();
    };

    const [ranges, setRanges] = useState<RangePickerItem[]>();

    const fmtSvc = useFmtSvc();
    const [selectedRange, setSelectedRange] = useState<RangeSelection>();
    const [type, setType] = useState('All');
    const discountQueryService = useDiMemo(DiscountQueryService);
    useEffect(() => {
        (async () => {
            const rangeData = await discountQueryService.getDiscountsRange(type);
            setRanges(rangeData.months.map((r) => ({ month: r })));

            const saved = await getLatest('discounts-overview');
            if (saved?.selectedRange) {
                setSelectedRange(reviveRangeSelection(saved.selectedRange));
            }
        })();
    }, []);

    useEffect(() => {
        if (ranges?.length && !selectedRange) {
            setSelectedRange(
                createSelectionRange(
                    getUniqueMonths(ranges, fmtSvc)
                        .map((m) => m.date)
                        .slice(-6)
                )
            );
        }
    }, [ranges, selectedRange]);

    const handleRangeSelected = useCallback(async (range: RangeSelection) => {
        const newSelection = createSelectionRange(range.months);
        setSelectedRange(newSelection);

        const configKey = 'discounts-overview';
        const { selectedRange, id } = await getLatest(configKey);
        await dashboardSvc.save(configKey, id, { selectedRange: newSelection, name: 'Default' } as IDashboardConfigBase);
    }, []);

    const getLatest = async (configKey: string) => {
        const configs = await dashboardSvc.getLayouts<{ selectedRange: RangeSelection } & IDashboardConfigBase>(configKey);
        const config = configs?.[0];
        return { selectedRange: config?.layout?.selectedRange, id: config?.id };
    };

    const { classes } = useFullHeightTabs({});
    return (
        <FillerSwitch loading={!selectedRange?.months?.length} noData={ranges?.length === 0} noDataMessage="Discount data is not yet available.">
            {() => (
                <Tabs defaultValue={'savings'} value={activeTab} onTabChange={handleTabChange} className={classes.tabs}>
                    <Tabs.List px="lg" sx={{ '[role=tab]': { alignSelf: 'end' } }}>
                        <Tabs.Tab value="savings" data-atid="DiscountsSavings">
                            Savings
                        </Tabs.Tab>
                        {/* <Tabs.Tab value="coverage" data-atid="DiscountsCoverage">
                            Coverage
                        </Tabs.Tab> */}
                        <Tabs.Tab value="inventory" data-atid="DiscountsInventory">
                            Inventory
                        </Tabs.Tab>
                        <Space w={1} sx={{ flex: 1 }} />
                        <Group py="xs">
                            <PlatformSelector selectedType={type} setType={setType} />
                            {activeTab === 'inventory' ? (
                                <FilterItem state="valid" corners={3} style={{ pointerEvents: 'none' }}>
                                    <FilterToken>Last 30 Days</FilterToken>
                                </FilterItem>
                            ) : (
                                <RangePickerButton onSelected={handleRangeSelected} selected={selectedRange!} ranges={ranges!} />
                            )}
                        </Group>
                    </Tabs.List>
                    <Tabs.Panel value="savings">{activeTab === 'savings' && <Savings platform={type} range={selectedRange!} />}</Tabs.Panel>
                    <Tabs.Panel value="coverage">{activeTab === 'coverage' && <Coverage platform={type} range={selectedRange!} />}</Tabs.Panel>
                    <Tabs.Panel value="inventory">{activeTab === 'inventory' && <Inventory platform={type} />}</Tabs.Panel>
                </Tabs>
            )}
        </FillerSwitch>
    );
}
endpoint('discounts-overview', DiscountsPage, 'Discounts');

function RangePickerButton({
    ranges,
    selected,
    onSelected,
}: {
    ranges: RangePickerItem[];
    selected: RangeSelection;
    onSelected: (item: RangeSelection) => void;
}) {
    const theme = useMantineTheme();
    const fmtSvc = useDi(FormatService);
    const [opened, { toggle, close }] = useDisclosure(false);
    const monthLabel =
        selected?.months.length > 1
            ? `${fmtSvc.formatShortMonthYear(selected.months[0])} - ${fmtSvc.formatLongMonthYear(selected.months[selected.months.length - 1])}`
            : selected?.months.length === 1
            ? fmtSvc.formatLongMonthYear(selected.months[0])
            : 'None';
    const handleChange = useCallback(
        (newSelection: RangeSelection) => {
            if (JSON.stringify(newSelection) !== JSON.stringify(selected)) {
                onSelected(newSelection);
            }
            close();
        },
        [close, onSelected, selected]
    );

    return (
        <>
            <FilterItem state="valid" corners={3} onClick={toggle} style={{ color: theme.colors.primary[6] }}>
                <FilterToken maxWidth={30}>
                    <i className="ti ti-calendar" />
                </FilterToken>
                <FilterToken>{monthLabel}</FilterToken>
                <FilterToken maxWidth={30}>
                    <i className="ti ti-chevron-down" />
                </FilterToken>
            </FilterItem>
            <Drawer position="right" opened={opened} onClose={close} padding={0} size={400} withCloseButton={false}>
                <RangePicker ranges={ranges} selected={selected} onSelected={handleChange} onClose={close} />
            </Drawer>
        </>
    );
}

function RangePicker({
    ranges,
    selected,
    onSelected,
    onClose,
}: {
    ranges: RangePickerItem[];
    selected: RangeSelection;
    onSelected: (item: RangeSelection) => void;
    onClose: () => void;
}) {
    const fmtSvc = useDi(FormatService);
    const theme = useMantineTheme();
    const [selection, setSelection] = useState<RangeSelection>({ ...selected });

    useEffect(() => {
        if (JSON.stringify(selection) !== JSON.stringify(selected)) {
            setSelection({ ...selected });
        }
    }, [selected]);
    const availableMonths = useMemo(() => getUniqueMonths(ranges, fmtSvc).reverse(), [ranges]);
    const updateMonths = useCallback(
        (from?: string, to?: string) => {
            const fromMonth = from ? startOfMonth(fmtSvc.fromMonthYear(from)) : selection.months[0];
            const toMonth = to ? fmtSvc.fromMonthYear(to) : selection.months[selection.months.length - 1];
            const months: Date[] = [fromMonth.getTime() > toMonth.getTime() ? toMonth : fromMonth];
            const endMonth = fromMonth.getTime() > toMonth.getTime() ? fromMonth : toMonth;
            let month = addMonths(months[0], 1);
            while (month <= endMonth) {
                months.push(month);
                month = addMonths(month, 1);
            }
            setSelection((s) => ({
                ...s,
                months,
            }));
        },
        [selection]
    );

    const [from, to] = useMemo(
        () =>
            (!selection.months.length ? [] : [selection.months[0], selection.months[selection.months.length - 1]]).map((d) =>
                fmtSvc.formatYearMonth(d)
            ),
        [selection]
    );

    const updateMonthFrom = useCallback((month: string) => updateMonths(month), [updateMonths]);
    const updateMonthTo = useCallback((month: string) => updateMonths(undefined, month), [updateMonths]);

    const apply = useCallback(() => onSelected(selection), [selection, onSelected]);
    const reset = useCallback(() => setSelection({ ...selected }), [selected]);

    const canApply = selection.months.length > 0;
    return (
        <Stack sx={{ height: '100%' }}>
            <Box>
                <Group position="apart" p="lg">
                    <Title order={4}>Months Selection</Title>
                    <CloseButton onClick={onClose} />
                </Group>
                <Divider />
            </Box>
            <Box p="lg" sx={{ flex: 1 }}>
                <Card sx={{ background: theme.colors.gray[2], position: 'static' }}>
                    <Select label="From" data={availableMonths} value={from} onChange={updateMonthFrom}></Select>
                    <Select label="To" data={availableMonths} value={to} onChange={updateMonthTo}></Select>
                </Card>
            </Box>
            <Box sx={{ background: theme.colors.gray[3] }}>
                <Divider />
                <Group position="apart" p="lg">
                    <Button variant="outline" onClick={reset}>
                        Reset
                    </Button>
                    <Group>
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button disabled={!canApply} onClick={apply}>
                            Apply
                        </Button>
                    </Group>
                </Group>
            </Box>
        </Stack>
    );
}

function getUniqueMonths(ranges: RangePickerItem[], fmtSvc: FormatService) {
    const monthsAdded = new Set<string>();
    const result: { label: string; value: string; date: Date }[] = [];
    const months = ranges.map((r) => r.month).sort((a, b) => a.getTime() - b.getTime());
    for (const month of months) {
        const label = fmtSvc.formatLongMonthYear(month);
        if (!monthsAdded.has(label)) {
            monthsAdded.add(label);
            result.push({ label, value: fmtSvc.formatYearMonth(month), date: month });
        }
    }
    return result;
}

interface PlatformSelectorProps {
    selectedType: string;
    setType: (type: string) => void;
}
function PlatformSelector({ ...props }: PlatformSelectorProps) {
    const discountQuerySvc = useDiMemo(DiscountQueryService);
    const baseOptions = useMemo(() => {
        return [
            { label: 'All', value: 'All' },
            { label: 'AWS', value: 'Aws' },
            { label: 'Azure', value: 'Azure' },
            { label: 'GCP', value: 'Gcp' },
        ].map((o) => ({ ...o, select: () => props.setType(o.value) }));
    }, [props.setType]);
    const [{ result: platforms, status }, { execute: getPlatforms }] = useAsync(() => discountQuerySvc.getConnectedPlatforms());
    useEffect(() => {
        getPlatforms();
    }, []);
    const options = useMemo(() => {
        return baseOptions.filter((o) => o.value === 'All' || platforms?.includes(o.value));
    }, [baseOptions, platforms, status]);

    return options.length <= 2 ? (
        <></>
    ) : (
        <Box>
            <TooltipWhite label="Filter by Cloud Provider">
                <Button.Group>
                    {options.map(({ label, value, select }) => (
                        <Button
                            key={value}
                            data-atid={`platformSelectorShow${label}Button`}
                            size="xs"
                            variant={props.selectedType === value ? 'filled' : 'outline'}
                            onClick={select}
                        >
                            {label}
                        </Button>
                    ))}
                </Button.Group>
            </TooltipWhite>
        </Box>
    );
}
